import { Add, ChevronLeft, Edit, Store, Visibility } from '@mui/icons-material';
import {
    Avatar,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Fab,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import DialogOffer from '../components/DialogOffer';
import { EmptyContentComponent } from '../../../components/Content';
import { LoadingContent } from '../../../components/Loading';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';

export default function AdvertiserDetails() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { search, input, handleChange: handleSearchChange } = useSearchTextInput();
    const { setErrors, enqueueSnackbar } = useErrors();
    const [offerDialogOpen, setOfferDialogOpen] = useState(false);
    const [editingOffer, setEditingOffer] = useState(null);

    const page = parseInt(searchParams.get('page')) || 1;

    const { data: advertiser, isLoading: isLoadingAdvertiser, mutate: mutateAdvertiser } = useFetch(`club/advertisers/${id}`);

    const offersParams = useMemo(
        () => ({
            params: {
                page,
                per_page: 10,
                search: search || undefined,
            },
        }),
        [page, search],
    );

    const { data: offersData, isLoading: isLoadingOffers, mutate: mutateOffers } = useFetch(
        `club/advertisers/${id}/offers`,
        offersParams,
    );

    const handleCloseOfferDialog = () => {
        setEditingOffer(null);
        setOfferDialogOpen(false);
    };

    const handleOpenOfferDialog = offer => {
        setEditingOffer(offer);
        setOfferDialogOpen(true);
    };

    const handleCreateNewOffer = () => {
        setEditingOffer(null);
        setOfferDialogOpen(true);
    };

    const formatStatus = approved => {
        if (approved === true) return { label: 'Aprovado', color: 'success' };
        if (approved === false) return { label: 'Pendente', color: 'warning' };
        return { label: 'Indefinido', color: 'default' };
    };

    if (isLoadingAdvertiser) return <LoadingContent />;

    if (!advertiser) {
        return (
            <Stack spacing={2}>
                <Typography variant="h6">Anunciante não encontrado</Typography>
            </Stack>
        );
    }

    const status = formatStatus(advertiser.approved);

    return (
        <Stack spacing={3}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton component={Link} to="/clube/anunciantes">
                    <ChevronLeft />
                </IconButton>
                <Typography variant="h4" fontWeight="bold">
                    Detalhes do Anunciante
                </Typography>
            </Stack>

            <Card>
                <CardContent>
                    <Stack direction="row" spacing={3} alignItems="flex-start">
                        <Avatar
                            src={advertiser.image}
                            sx={{
                                borderRadius: 2,
                                width: 100,
                                height: 100,
                                bgcolor: 'primary.main',
                            }}
                        >
                            <Store sx={{ fontSize: 50 }} />
                        </Avatar>
                        <Stack spacing={1} flex={1}>
                            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                                <Typography variant="h5">{advertiser.name}</Typography>
                                <Chip label={status.label} color={status.color} size="small" />
                            </Stack>
                            {advertiser.businessName && (
                                <Typography variant="body1" color="text.secondary">
                                    {advertiser.businessName}
                                </Typography>
                            )}
                            {advertiser.description && (
                                <Typography variant="body2" color="text.secondary">
                                    {advertiser.description}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                {advertiser.city && advertiser.state && (
                                    <Typography variant="body2" color="text.secondary">
                                        📍 {advertiser.city} - {advertiser.state}
                                    </Typography>
                                )}
                                {advertiser.phone && (
                                    <Typography variant="body2" color="text.secondary">
                                        📞 {advertiser.phone}
                                    </Typography>
                                )}
                                {advertiser.responsibleEmail && (
                                    <Typography variant="body2" color="text.secondary">
                                        ✉️ {advertiser.responsibleEmail}
                                    </Typography>
                                )}
                            </Stack>
                            {advertiser.website && (
                                <Typography variant="body2">
                                    <a href={advertiser.website} target="_blank" rel="noopener noreferrer">
                                        {advertiser.website}
                                    </a>
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <Card>
                <Stack p={2} spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Ofertas
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Chip
                                color="primary"
                                variant="outlined"
                                label={`${offersData?.numRows ?? 0} oferta${offersData?.numRows !== 1 ? 's' : ''}`}
                                sx={{ fontWeight: 500 }}
                            />
                            <Button variant="contained" startIcon={<Add />} onClick={handleCreateNewOffer}>
                                Adicionar oferta
                            </Button>
                        </Stack>
                    </Stack>
                    <TextField
                        fullWidth
                        label="Buscar oferta"
                        placeholder="Nome ou descrição"
                        value={input}
                        onChange={event => handleSearchChange(event.target.value)}
                    />
                </Stack>
                <Divider />

                {isLoadingOffers ? (
                    <LoadingContent />
                ) : (
                    <>
                        {offersData?.rows?.length === 0 ? (
                            <Paper sx={{ border: 0, p: 4 }}>
                                <EmptyContentComponent message="Nenhuma oferta encontrada" />
                            </Paper>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>Valor</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {offersData?.rows?.map(offer => (
                                        <TableRow key={offer.id}>
                                            <TableCell>{offer.id}</TableCell>
                                            <TableCell>
                                                <Stack>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {offer.name}
                                                    </Typography>
                                                    {offer.label && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {offer.label}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                {offer.isPercentual ? (
                                                    <Typography variant="body2">{offer.value}%</Typography>
                                                ) : (
                                                    <Typography variant="body2">
                                                        R$ {Number(offer.value).toFixed(2).replace('.', ',')}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={offer.status ? 'Ativo' : 'Inativo'}
                                                    color={offer.status ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenOfferDialog(offer)}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {offersData?.pages > 1 && (
                            <Stack direction="row" justifyContent="center" p={2}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button
                                        variant="outlined"
                                        disabled={page === 1}
                                        onClick={() => {
                                            const nextParams = new URLSearchParams(searchParams);
                                            nextParams.set('page', page - 1);
                                            setSearchParams(nextParams);
                                        }}
                                    >
                                        Anterior
                                    </Button>
                                    <Typography>
                                        Página {page} de {offersData?.pages}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        disabled={page >= offersData?.pages}
                                        onClick={() => {
                                            const nextParams = new URLSearchParams(searchParams);
                                            nextParams.set('page', page + 1);
                                            setSearchParams(nextParams);
                                        }}
                                    >
                                        Próxima
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </>
                )}
            </Card>

            <Fab
                color="primary"
                sx={{ position: 'fixed', bottom: 32, right: 32 }}
                onClick={handleCreateNewOffer}
            >
                <Add />
            </Fab>

            <DialogOffer
                open={offerDialogOpen}
                onClose={handleCloseOfferDialog}
                offer={editingOffer}
                advertiserId={id}
                onSuccess={() => mutateOffers()}
            />
        </Stack>
    );
}
